import React, {useContext, useEffect, useMemo, useState} from 'react'
import {createPortal} from 'react-dom'
import {useSelector} from 'react-redux'
import {useLocation} from 'react-router-dom'
import logo from '../../assets/images/logo.png'
import {RootState} from '../../types/store'
import {getSafeHttpUrl} from '../../utils/common'
import AnnouncementReadyContext from './readiness'
import styles from './index.module.scss'

type AnnouncementScene = 'home' | 'recharge'

interface Props {
    scene: AnnouncementScene
}

const shownWithoutStorage = new Set<string>()

const getStorageKey = (scene: AnnouncementScene) => `smhub:announcement:${scene}:shown`

const hasBeenShown = (key: string) => {
    try {
        return sessionStorage.getItem(key) === '1'
    } catch (error) {
        return shownWithoutStorage.has(key)
    }
}

const markAsShown = (key: string) => {
    shownWithoutStorage.add(key)
    try {
        sessionStorage.setItem(key, '1')
    } catch (error) {
        // Some privacy modes disable sessionStorage; the in-memory fallback still
        // prevents duplicate dialogs until this page is refreshed.
    }
}

const AnnouncementModal: React.FC<Props> = ({scene}) => {
    const ready = useContext(AnnouncementReadyContext)
    const [visible, setVisible] = useState(false)
    const location = useLocation()
    const {config, userInfo} = useSelector((state: RootState) => state.app)
    const isActiveScene = scene === 'home'
        ? location.pathname === '/' || location.pathname === '/home'
        : location.pathname === '/recharge'
    const safeUrls = useMemo(() => {
        return (config.backup_site_url || [])
            .map((url) => ({label: url, href: getSafeHttpUrl(url)}))
            .filter((item): item is {label: string, href: string} => item.href !== null)
    }, [config.backup_site_url])

    useEffect(() => {
        if (!isActiveScene || !config.announcement_enabled) {
            setVisible(false)
            return
        }

        if (!ready || !userInfo.username || !userInfo.password) {
            return
        }

        const storageKey = getStorageKey(scene)
        if (hasBeenShown(storageKey)) {
            return
        }

        markAsShown(storageKey)
        setVisible(true)
    }, [config.announcement_enabled, isActiveScene, ready, scene, userInfo.password, userInfo.username])

    useEffect(() => {
        if (!visible) {
            return
        }

        const previousOverflow = document.body.style.overflow
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setVisible(false)
            }
        }

        document.body.style.overflow = 'hidden'
        document.addEventListener('keydown', closeOnEscape)

        return () => {
            document.body.style.overflow = previousOverflow
            document.removeEventListener('keydown', closeOnEscape)
        }
    }, [visible])

    if (!visible) {
        return null
    }

    const close = () => setVisible(false)

    return createPortal(
        <div className={styles.overlay}>
            <section
                className={styles.dialog}
                role="dialog"
                aria-modal="true"
                aria-labelledby={`announcement-title-${scene}`}
            >
                <header className={styles.header}>
                    <span className={styles.logoWrap}>
                        <img className={styles.logo} src={logo} alt="SMT.club" />
                    </span>
                    <button className={styles.close} type="button" onClick={close} aria-label="关闭公告">
                        &times;
                    </button>
                </header>
                <div className={styles.body}>
                    <h2 className={styles.title} id={`announcement-title-${scene}`}>请保存账号密码</h2>
                    <div className={styles.credentials}>
                        <div className={styles.credentialRow}>
                            <span className={styles.credentialLabel}>账号：</span>
                            <span className={styles.credentialValue}>{userInfo.username}</span>
                        </div>
                        <div className={styles.credentialRow}>
                            <span className={styles.credentialLabel}>密码：</span>
                            <span className={styles.credentialValue}>{userInfo.password}</span>
                        </div>
                    </div>
                    <p className={styles.tip}>请截图保存，防止账号丢失！</p>

                    {safeUrls.length > 0 && (
                        <div className={styles.section}>
                            <h3 className={styles.sectionTitle}>备用网址：</h3>
                            <div className={styles.linkList}>
                                {safeUrls.map((item, index) => (
                                    <a
                                        className={styles.link}
                                        href={item.href}
                                        key={`${item.href}-${index}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {item.label}
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}

                    {config.permanent_email && (
                        <div className={styles.section}>
                            <h3 className={styles.sectionTitle}>永久邮箱：</h3>
                            <p className={styles.email}>{config.permanent_email}</p>
                        </div>
                    )}

                    {config.announcement_description && (
                        <p className={styles.description}>{config.announcement_description}</p>
                    )}
                </div>
                <button className={styles.confirm} type="button" onClick={close}>我已截图</button>
            </section>
        </div>,
        document.body
    )
}

export default AnnouncementModal
